import {defineMessages} from "react-intl";
import {FileMetadata} from "../common/model";
import {SpatialModel} from "../model";

export enum StyleType {
  RASTER = "Raster",
  VECTOR = "Vector",
  MIXED = "Mixed",
  UNKNOWN = "Unknown",
}

export interface Style extends SpatialModel {
  filePath?: string;
  createdBy?: {
    username: string;
  };
  creationTime?: string;
  updateTime?: string;
  content?: string;
  type: StyleType;
  fileMetadata?: FileMetadata;
}

export interface IchParameter {
  name: string;
  selectedIch: string;
  value: any;
  error?: string;
}

export interface DefaultStretchParameters {
  name: string,
  defaultValue: string,
  parameters?: DefaultStretchParameter[];
}

export interface DefaultStretchParameter {
  name: string;
  description?: string;
}

export interface ColorRamp {
  name: string;
  description: string;
  graphic: string;
  fileName: string;
}

export interface IchConfig {
  imageChainProviders: ImageChainProviders;
}

export interface ImageChainProviders {
  providers: Provider[];
}

export interface Provider {
  id: string;
  imageChains: ImageChain[];
}

export interface ImageChain {
  name: string;
  parameters: Parameter[];
  requiredNumberOfBands: number;
  title: string;
}

export interface Parameter {
  dataTypes: DataType;
  description: string;
  name: string;
}

export interface DataType {
  attributes: Attribute[];
  defaultValue: any;
  name: string;
}

export interface Attribute {
  type: string;
  value: any;
}

export const MODEL_MESSAGES = defineMessages({
  defaultMetaTitle: {
    id: "studio.styles.default-metadata-style",
    defaultMessage: "DEFAULT STYLE",
  },
  noMetaTitle: {
    id: "studio.styles.no-metadata-style",
    defaultMessage: "NO NAME",
  },
});

export const DEFAULT_STYLE: Style = {
  id: null,
  name: MODEL_MESSAGES.defaultMetaTitle.defaultMessage,
  title: MODEL_MESSAGES.defaultMetaTitle.defaultMessage,
  abstractText: "",
  wgs84Bounds: null,
  type: null,
};

export const DEFAULT_STRETCH_PARAMETERS: DefaultStretchParameters[] = [
  {
    name: "BasicGaussian",
    defaultValue: "Float (Table (2: 127.5,200))",
    parameters: [
      {name: "Mean", description: "The center of the distribution"},
      {name: "Width", description: "Number of bins from the Mean bracketing 95% of the distribution"}
    ]
  },
  {
    name: "Constant Value",
    defaultValue: "Float (Table (1: 128))",
    parameters: [
      {name: "Value", description: "The constant value"}
    ]
  },
  {
    name: "EqualClip",
    defaultValue: "Float (Table (1: 2.5))",
    parameters: [
      {name: "Percent", description: "The percent to clip from both sides"}
    ]
  },
  {
    name: "Gamma",
    defaultValue: "Float (Table (2: 2.2,5.03))",
    parameters: [
      {name: "Gamma", description: "The parameter of the curve"},
      {name: "Scale", description: "Scale factor"}
    ]
  },
  {
    name: "Gaussian",
    defaultValue: "Float (Table (8: 8,242,8,242,8,242,127.5,200))",
    parameters: [
      {name: "Exclude Low", description: "Low limit of data to be processed"},
      {name: "Exclude High", description: "High limit of data to be processed"},
      {name: "From Low"},
      {name: "From High"},
      {name: "To Low"},
      {name: "To High"},
      {name: "Mean"},
      {name: "Width"}
    ]
  },
  {
    name: "HistogramEqualization",
    defaultValue: "Float (Table (1: 256))",
    parameters: [
      {name: "Number Of Bins", description: "The number of bins for histogram equalization"}
    ]
  },
  {
    name: "Identity",
    defaultValue: ""
  },
  {
    name: "LeftRightClip",
    defaultValue: "Float (Table (2: 2.5,99.0))",
    parameters: [
      {name: "Left Percent", description: "The percent to clip from the left side"},
      {name: "Right Percent", description: "The percent to clip from the right side"}
    ]
  },
  {
    name: "LevelSlice",
    defaultValue: "Float (Table (3: 2,0,255))",
    parameters: [
      {name: "Number Of Levels", description: "The number of levels to slice"},
      {name: "Starting Value", description: "LUT output value for lowest level"},
      {name: "Ending Value", description: "LUT output value for highest level"}
    ]
  },
  {
    name: "Linear",
    defaultValue: "Float (Table (2: 1.09,8.0))",
    parameters: [
      {name: "Slope", description: "The slope of the LUT graph"},
      {name: "Shift", description: "The offset of the LUT graph"}
    ]
  },
  {
    name: "Logarithmic",
    defaultValue: ""
  },
  {
    name: "FullRange",
    defaultValue: ""
  },
  {
    name: "NoStretch",
    defaultValue: ""
  },
  {
    name: "StandardDeviation",
    defaultValue: "Float (Table (1: 2.0))",
    parameters: [
      {name: "Standard Deviations", description: "The number of standard deviations to stretch between"}
    ]
  },
  {
    name: "TwoPointLinear",
    defaultValue: "Float (Table (2: 8.00,242.00))",
    parameters: [
      {name: "Gray Min", description: "The minimum value for all bands"},
      {name: "Gray Max", description: "The maximum value for all bands"}
    ]
  },
];